import { SYNC_TABLES } from '@quickreport/db';
import { NIL_ORG } from './queue';
import type { LocalDb, Tx } from './types';

/**
 * A backup is the rows of this computer's database as JSON, not a copy of the database files.
 * That keeps it readable by later versions of the app, and lets it be checked without opening it.
 */
export const BACKUP_FORMAT = 1;

/** Local-only tables that hold work not yet on the server, or images waiting to upload. */
const LOCAL_TABLES = ['outbox', 'sync_conflicts', 'file_blobs', 'sync_state'] as const;

/** Restore in this order and delete in the reverse. */
export const BACKUP_TABLES: string[] = [...SYNC_TABLES.map((t) => t.name), ...LOCAL_TABLES];

export interface BackupFile {
  format: typeof BACKUP_FORMAT;
  app: 'quickreport';
  createdAt: string;
  orgId: string;
  orgName: string;
  /** How many changes were still waiting to reach the server. */
  pending: number;
  counts: Record<string, number>;
  tables: Record<string, Record<string, unknown>[]>;
}

/**
 * Device-specific settings are left out. A restored computer signs in again, registers as a
 * new computer, and gets its own run of lab numbers, so no number is ever handed out twice.
 */
const DEVICE_ONLY = "(key = 'auth' or key = 'device_id' or key like 'nb:%')";

type Q = Pick<LocalDb, 'query'> | Tx;

export async function exportBackup(
  db: Q,
  info: { orgId: string; orgName: string; now?: Date },
): Promise<BackupFile> {
  const tables: BackupFile['tables'] = {};
  const counts: Record<string, number> = {};
  for (const name of BACKUP_TABLES) {
    const where =
      name === 'sync_state'
        ? ` where org_id in ('${info.orgId}', '${NIL_ORG}') and not ${DEVICE_ONLY}`
        : '';
    const r = await db.query<{ rows: Record<string, unknown>[] }>(
      `select coalesce(jsonb_agg(to_jsonb(t)), '[]'::jsonb) as rows from ${name} t${where}`,
    );
    const rows = r.rows[0]?.rows ?? [];
    tables[name] = rows;
    counts[name] = rows.length;
  }
  return {
    format: BACKUP_FORMAT,
    app: 'quickreport',
    createdAt: (info.now ?? new Date()).toISOString(),
    orgId: info.orgId,
    orgName: info.orgName,
    pending: counts.outbox ?? 0,
    counts,
    tables,
  };
}

export class BackupError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BackupError';
  }
}

/** Throws a message a person can act on when the file is not a usable backup. */
export function checkBackup(input: unknown): BackupFile {
  const b = input as Partial<BackupFile> | null;
  if (!b || typeof b !== 'object' || b.app !== 'quickreport' || !b.tables || !b.orgId) {
    throw new BackupError('That file is not a LabView backup.');
  }
  if (b.format !== BACKUP_FORMAT) {
    throw new BackupError(
      'That backup was made by a different version of LabView. Update the app and try again.',
    );
  }
  for (const [name, rows] of Object.entries(b.tables)) {
    if (!BACKUP_TABLES.includes(name))
      throw new BackupError(`The backup has an unknown table "${name}".`);
    if (!Array.isArray(rows)) throw new BackupError(`The backup is damaged (table "${name}").`);
  }
  return b as BackupFile;
}

/**
 * Replace everything in this database with the backup, in one transaction: either all of it is
 * restored or none of it. Columns the backup does not have keep their defaults, so a backup from
 * an older version still restores into a newer database.
 */
export async function restoreBackup(db: LocalDb, input: unknown): Promise<BackupFile> {
  const backup = checkBackup(input);
  await db.transaction(async (tx) => {
    for (const name of [...BACKUP_TABLES].reverse()) await tx.query(`delete from ${name}`);
    for (const name of BACKUP_TABLES) {
      const rows = backup.tables[name] ?? [];
      if (rows.length === 0) continue;
      const cols = await tx.query<{ column_name: string }>(
        `select column_name from information_schema.columns
          where table_schema = 'public' and table_name = $1`,
        [name],
      );
      const have = new Set(cols.rows.map((c) => c.column_name));
      const present = Object.keys(rows[0] ?? {}).filter((k) => have.has(k));
      const list = present.map((c) => `"${c}"`).join(', ');
      // The outbox order number is generated; the backup carries the original order.
      const overriding = name === 'outbox' ? ' overriding system value' : '';
      await tx.query(
        `insert into ${name} (${list})${overriding}
         select ${list} from jsonb_populate_recordset(null::${name}, $1::jsonb)`,
        [JSON.stringify(rows)],
      );
    }
    await tx.query(
      `select setval(pg_get_serial_sequence('outbox', 'seq'), coalesce((select max(seq) from outbox), 0) + 1, false)`,
    );
  });
  return backup;
}

/** Short text for the list of backups: what is in it and how much is still unsent. */
export function describeBackup(b: Pick<BackupFile, 'counts' | 'pending'>): string {
  const reports = b.counts.reports ?? 0;
  const patients = b.counts.patients ?? 0;
  const parts = [
    `${reports} report${reports === 1 ? '' : 's'}`,
    `${patients} patient${patients === 1 ? '' : 's'}`,
  ];
  if (b.pending > 0)
    parts.push(`${b.pending} change${b.pending === 1 ? '' : 's'} not yet on the server`);
  return parts.join(', ');
}
